import '../../styles/_support-panel.scss';

import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import type { LaunchSegment, Video } from '../../datasource/generated';
import { useGetCmsSettingsQuery } from '../../datasource/queries/cmsSettings';
import { useAppDispatch, useAppSelector } from '../../hooks';
import openMailtoLink from '../../utility/openMailtoLink';
import { getPlayerDiagnosticsInterpretations, probeMediaRangeSupport } from '../../utility/playerDiagnostics';
import {
    ExternalCommand,
    resetPlayerDiagnosticsTestResults,
    setExternalCommand,
    setExternalSeekCommand,
    setPlayerDiagnosticsActive,
    setPlayerDiagnosticsRangeProbe,
} from '../Store/playerSlice';

type DiagnosticTone = 'neutral' | 'success' | 'warning' | 'error';

const DEFAULT_DIAGNOSTICS_SUPPORT_EMAIL_TO = 'info@pli.edu';
const DEFAULT_DIAGNOSTICS_SUPPORT_EMAIL_SUBJECT = 'Watch Player Diagnostics';

const formatSeconds = (value?: number | null) => `${Math.max(0, Math.round(value ?? 0))}s`;

const formatDebugValue = (value?: number | string | null) => {
    if (value === null || value === undefined || value === '') {
        return 'Not available';
    }

    return `${value}`;
};

const formatOptionalMessage = (value?: string | null) => {
    if (value === null || value === undefined || value === '') {
        return 'None';
    }

    return value;
};

const formatBoolean = (value?: boolean | null) => {
    if (value === null || value === undefined) {
        return 'Not available';
    }

    return value ? 'Yes' : 'No';
};

const formatTimeRangeSnapshot = ({
    start,
    end,
    length,
}: {
    start?: number | null;
    end?: number | null;
    length?: number | null;
}) => {
    if (!length || start === null || start === undefined || end === null || end === undefined) {
        return 'None';
    }

    return `${formatSeconds(start)} - ${formatSeconds(end)} (${length} range${length === 1 ? '' : 's'})`;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getVideoExport = (video?: Video | null) => {
    if (!video) {
        return null;
    }

    return {
        url: video.url ?? null,
        cdn: video.cdn ?? null,
        streaming: video.streaming ?? null,
        closedCaptions: video.closedCaptions ?? null,
        transcription: video.transcription ?? null,
        cpeMarkers: video.cpeMarkers ?? null,
        authCodes: video.authCodes ?? null,
        position: video.position ?? null,
    };
};

const getLaunchSegmentExport = (segment: LaunchSegment) => ({
    itemSk: segment.itemSk ?? null,
    itemPk: segment.itemPk ?? null,
    prgSegmentSk: segment.prgSegmentSk ?? null,
    producedRunningTime: segment.producedRunningTime ?? null,
    video: getVideoExport(segment.video),
    isPurchased: segment.isPurchased ?? null,
});

const getStatusIcon = (tone: DiagnosticTone) => {
    if (tone === 'success') {
        return 'checkmark';
    }

    if (tone === 'warning' || tone === 'error') {
        return 'alert';
    }

    return null;
};

const getRangeProbeStatus = (status: string): { label: string; tone: DiagnosticTone } => {
    switch (status) {
        case 'supported':
            return { label: 'Supported', tone: 'success' };
        case 'checking':
            return { label: 'Probe in progress', tone: 'neutral' };
        case 'unsupported':
            return { label: 'Unsupported', tone: 'error' };
        case 'error':
            return { label: 'Probe failed', tone: 'error' };
        case 'not-applicable':
            return { label: 'Not applicable', tone: 'neutral' };
        default:
            return { label: 'Not checked', tone: 'neutral' };
    }
};

const getSeekStatus = (status: string): { label: string; tone: DiagnosticTone } => {
    switch (status) {
        case 'succeeded':
            return { label: 'Success', tone: 'success' };
        case 'pending':
            return { label: 'Seek in progress', tone: 'neutral' };
        case 'failed':
            return { label: 'Seek failed', tone: 'error' };
        default:
            return { label: 'No seek result', tone: 'neutral' };
    }
};

const getCommandStatus = (status: string): { label: string; tone: DiagnosticTone } => {
    switch (status) {
        case 'succeeded':
            return { label: 'Success', tone: 'success' };
        case 'requested':
            return { label: 'Command in progress', tone: 'neutral' };
        case 'failed':
            return { label: 'Command failed', tone: 'error' };
        default:
            return { label: 'No command result', tone: 'neutral' };
    }
};

const buildMailtoLink = ({
    to,
    subject,
    body,
}: {
    to: string;
    subject: string;
    body: string;
}) => `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

const getInterpretationSummary = (tones: DiagnosticTone[]): { label: string; tone: DiagnosticTone } => {
    if (tones.includes('error')) {
        return { label: 'Action needed', tone: 'error' };
    }

    if (tones.includes('warning')) {
        return { label: 'Review results', tone: 'warning' };
    }

    if (tones.includes('success')) {
        return { label: 'Healthy', tone: 'success' };
    }

    return { label: 'Informational', tone: 'neutral' };
};

const getInterpretationToneLabel = (tone: DiagnosticTone) => {
    switch (tone) {
        case 'success':
            return 'Healthy';
        case 'warning':
            return 'Review';
        case 'error':
            return 'Action needed';
        default:
            return 'Info';
    }
};

const getReadyStateLabel = (readyState?: number | null) => {
    switch (readyState) {
        case 0:
            return 'Have nothing (0)';
        case 1:
            return 'Have metadata (1)';
        case 2:
            return 'Have current data (2)';
        case 3:
            return 'Have future data (3)';
        case 4:
            return 'Have enough data (4)';
        default:
            return 'Not available';
    }
};

const getNetworkStateLabel = (networkState?: number | null) => {
    switch (networkState) {
        case 0:
            return 'Empty (0)';
        case 1:
            return 'Idle (1)';
        case 2:
            return 'Loading (2)';
        case 3:
            return 'No source (3)';
        default:
            return 'Not available';
    }
};

const StatusBadge = ({
    label,
    tone,
}: {
    label: string;
    tone: DiagnosticTone;
}) => {
    const icon = getStatusIcon(tone);

    return (
        <span className={`support-panel__status-badge support-panel__status-badge--${tone}`}>
            {icon && <i aria-hidden={true} className={`support-panel__status-icon fa-kit fa-${icon}`} />}
            <span>{label}</span>
        </span>
    );
};

const StatusIcon = ({
    label,
    tone,
}: {
    label: string;
    tone: DiagnosticTone;
}) => {
    const icon = getStatusIcon(tone);

    return (
        <span
            aria-label={label}
            className={`support-panel__status-marker support-panel__status-marker--${tone}`}
            role="img"
            title={label}
        >
            {icon ? (
                <i aria-hidden={true} className={`support-panel__status-marker-icon fa-kit fa-${icon}`} />
            ) : (
                <span aria-hidden={true} className="support-panel__status-marker-dash">-</span>
            )}
            <span className="support-panel__visually-hidden">{label}</span>
        </span>
    );
};

const EmailIcon = () => (
    <span aria-hidden={true} className="support-panel__diagnostics-email-icon">
        <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M2.25 4.25H13.75V11.75H2.25V4.25ZM2.25 4.5L8 8.5L13.75 4.5"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.25"
            />
        </svg>
    </span>
);

const DiagnosticCard = ({
    title,
    status,
    action,
    headerButton,
    expandHeaderButtonOnClick = false,
    showToggle = true,
    isExpandable = true,
    children,
    defaultExpanded = true,
}: {
    title: string;
    status?: ReactNode;
    action?: ReactNode;
    headerButton?: {
        ariaLabel: string;
        onClick(): void;
    };
    expandHeaderButtonOnClick?: boolean;
    showToggle?: boolean;
    isExpandable?: boolean;
    children?: ReactNode;
    defaultExpanded?: boolean;
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const panelId = `support-panel-diagnostics-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

    return (
        <section className={`support-panel__diagnostics-card ${isExpanded ? 'support-panel__diagnostics-card--expanded' : ''}`}>
            <div className="support-panel__diagnostics-card-header">
                {headerButton ? (
                    <>
                        <button
                            aria-controls={isExpandable ? panelId : undefined}
                            aria-expanded={isExpandable ? isExpanded : undefined}
                            aria-label={headerButton.ariaLabel}
                            className="support-panel__diagnostics-card-main-button"
                            onClick={() => {
                                if (expandHeaderButtonOnClick && isExpandable) {
                                    setIsExpanded(true);
                                }

                                headerButton.onClick();
                            }}
                            type="button"
                        >
                            <h4 className="paragraph-1--medium">{title}</h4>
                            {(status || action) && (
                                <span className="support-panel__diagnostics-card-header-controls">
                                    {status}
                                    {action}
                                </span>
                            )}
                        </button>
                        {isExpandable && showToggle && (
                            <button
                                aria-controls={panelId}
                                aria-expanded={isExpanded}
                                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title}`}
                                className="support-panel__diagnostics-card-toggle"
                                onClick={() => setIsExpanded((currentValue) => !currentValue)}
                                type="button"
                            >
                                <i
                                    aria-hidden={true}
                                    className={`support-panel__diagnostics-card-chevron fa-kit fa-chevron-${isExpanded ? 'up' : 'down'}`}
                                />
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        <h4 className="paragraph-1--medium">{title}</h4>
                        <div className="support-panel__diagnostics-card-header-controls">
                            {status}
                            {action}
                            {isExpandable && showToggle && (
                                <button
                                    aria-controls={panelId}
                                    aria-expanded={isExpanded}
                                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title}`}
                                    className="support-panel__diagnostics-card-toggle"
                                    onClick={() => setIsExpanded((currentValue) => !currentValue)}
                                    type="button"
                                >
                                    <i
                                        aria-hidden={true}
                                        className={`support-panel__diagnostics-card-chevron fa-kit fa-chevron-${isExpanded ? 'up' : 'down'}`}
                                    />
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
            {isExpandable && isExpanded && children && (
                <div className="support-panel__diagnostics-card-body" id={panelId}>
                    {children}
                </div>
            )}
        </section>
    );
};

const DiagnosticRow = ({
    label,
    value,
    valueClassName = '',
}: {
    label: string;
    value: ReactNode;
    valueClassName?: string;
}) => (
    <div className="support-panel__diagnostic-row">
        <span className="support-panel__debug-label">{label}</span>
        <span className={valueClassName}>{value}</span>
    </div>
);

const DiagnosticSplitRow = ({
    firstLabel,
    firstValue,
    secondLabel,
    secondValue,
}: {
    firstLabel: string;
    firstValue: ReactNode;
    secondLabel: string;
    secondValue: ReactNode;
}) => (
    <div className="support-panel__diagnostic-row support-panel__diagnostic-row--split">
        <div className="support-panel__diagnostic-field">
            <span className="support-panel__debug-label">{firstLabel}</span>
            <span>{firstValue}</span>
        </div>
        <div className="support-panel__diagnostic-field">
            <span className="support-panel__debug-label">{secondLabel}</span>
            <span>{secondValue}</span>
        </div>
    </div>
);

export const PlayerDiagnostics = () => {
    const dispatch = useAppDispatch();
    const { data: watchSettings } = useGetCmsSettingsQuery();
    const individualSk = useAppSelector((state) => state.user.individualSK);
    const userName = useAppSelector((state) => state.user.name);
    const launchDetails = useAppSelector((state) => state.player.launchDetails);
    const currentSegmentItemSk = useAppSelector((state) => state.player.currentSegmentItemSk);
    const currentSegmentPlaybackProgress = useAppSelector((state) =>
        state.player.playbackProgress.find((entry) => entry.itemSk === currentSegmentItemSk)
    );
    const diagnostics = useAppSelector((state) => state.player.diagnostics);
    const videoSourceType = useAppSelector((state) => state.player.videoSourceType);

    const [isRunningRangeProbe, setIsRunningRangeProbe] = useState(false);
    const [showPlayerDiagnostics, setShowPlayerDiagnostics] = useState(false);
    const latestMediaFileRef = useRef(diagnostics.currentFile);
    const latestVideoSourceTypeRef = useRef(videoSourceType);
    const rangeProbeRequestIdRef = useRef(0);

    useEffect(() => {
        dispatch(setPlayerDiagnosticsActive(true));
        dispatch(resetPlayerDiagnosticsTestResults());

        return () => {
            dispatch(setPlayerDiagnosticsActive(false));
        };
    }, [dispatch]);

    useEffect(() => {
        latestMediaFileRef.current = diagnostics.currentFile;
        latestVideoSourceTypeRef.current = videoSourceType;
        rangeProbeRequestIdRef.current += 1;
        setIsRunningRangeProbe(false);
    }, [diagnostics.currentFile, videoSourceType]);

    const runRangeProbe = async () => {
        if (!diagnostics.currentFile) {
            return;
        }

        const requestId = rangeProbeRequestIdRef.current + 1;
        rangeProbeRequestIdRef.current = requestId;
        const probedFile = diagnostics.currentFile;
        const probedSourceType = videoSourceType;

        setIsRunningRangeProbe(true);

        dispatch(setPlayerDiagnosticsRangeProbe({
            status: 'checking',
            url: probedFile,
            sourceType: probedSourceType,
            responseStatus: undefined,
            acceptRanges: null,
            contentRange: null,
            contentLength: null,
            checkedAt: new Date().toISOString(),
            error: null,
        }));

        const result = await probeMediaRangeSupport(probedFile, probedSourceType);

        if (
            rangeProbeRequestIdRef.current !== requestId ||
            latestMediaFileRef.current !== probedFile ||
            latestVideoSourceTypeRef.current !== probedSourceType
        ) {
            return;
        }

        dispatch(setPlayerDiagnosticsRangeProbe(result));
        setIsRunningRangeProbe(false);
    };

    const hasLivePlaybackSnapshot =
        diagnostics.browserMedia.lastUpdatedAt !== null ||
        diagnostics.playbackState !== 'idle' ||
        diagnostics.currentDuration > 0 ||
        diagnostics.lastSeek.status !== 'idle' ||
        diagnostics.lastPlayPauseCommand.status !== 'idle';
    const currentPosition = hasLivePlaybackSnapshot
        ? diagnostics.currentPosition
        : currentSegmentPlaybackProgress?.position ?? 0;
    const currentDuration = diagnostics.currentDuration || currentSegmentPlaybackProgress?.totalTime || 0;
    const seekUpperBound = currentDuration > 2 ? currentDuration - 1 : Math.max(currentPosition, 0);
    const showSeekTest = true;
    const canSeek = Boolean(diagnostics.currentFile) && !launchDetails.isWebcast && currentDuration > 0;
    const rangeProbeStatus = getRangeProbeStatus(diagnostics.rangeProbe.status);
    const playPauseCommand = diagnostics.lastPlayPauseCommand;
    const lastCommandStatus = getCommandStatus(playPauseCommand.status);
    const lastSeekStatus = getSeekStatus(diagnostics.lastSeek.status);
    const isPlayDisabled = !diagnostics.currentFile ||
        diagnostics.playbackState === 'playing' ||
        diagnostics.playbackState === 'buffering';
    const isPauseDisabled = !diagnostics.currentFile ||
        diagnostics.playbackState === 'paused' ||
        diagnostics.playbackState === 'idle' ||
        diagnostics.playbackState === 'complete' ||
        diagnostics.playbackState === 'error';
    const interpretations = useMemo(() => getPlayerDiagnosticsInterpretations({
        diagnostics,
        videoSourceType,
    }), [diagnostics, videoSourceType]);
    const interpretationSummary = getInterpretationSummary(
        interpretations.map((interpretation) => interpretation.tone)
    );
    const launchDetailsExport = useMemo(() => ({
        itemSk: launchDetails.itemSk ?? null,
        itemPk: launchDetails.itemPk ?? null,
        currentTimeUtc: launchDetails.currentTimeUtc ?? null,
        currentSegment: launchDetails.currentSegment ?? null,
        currentPosition: launchDetails.currentPosition ?? null,
        video: getVideoExport(launchDetails.video),
        segments: launchDetails.segments?.map(getLaunchSegmentExport) ?? null,
        isPromoted: launchDetails.isPromoted ?? null,
    }), [
        launchDetails.currentPosition,
        launchDetails.currentSegment,
        launchDetails.currentTimeUtc,
        launchDetails.isPromoted,
        launchDetails.itemPk,
        launchDetails.itemSk,
        launchDetails.segments,
        launchDetails.video,
    ]);
    const playerDiagnosticsExport = useMemo(() => ({
        individualSk,
        currentSegmentItemSk,
        videoSourceType,
        diagnostics,
        currentSegmentPlaybackProgress,
        interpretations,
        interpretationSummary,
        launchDetails: launchDetailsExport,
    }), [
        individualSk,
        currentSegmentItemSk,
        currentSegmentPlaybackProgress,
        diagnostics,
        interpretationSummary,
        interpretations,
        videoSourceType,
        launchDetailsExport,
    ]);
    const playerDiagnosticsJson = useMemo(
        () => JSON.stringify(playerDiagnosticsExport, null, 4),
        [playerDiagnosticsExport]
    );
    const diagnosticsSupportEmailTo = watchSettings?.diagnosticsSupportEmailTo?.trim() || DEFAULT_DIAGNOSTICS_SUPPORT_EMAIL_TO;
    const diagnosticsSupportEmailSubject = watchSettings?.diagnosticsSupportEmailSubject?.trim() || DEFAULT_DIAGNOSTICS_SUPPORT_EMAIL_SUBJECT;
    const diagnosticsEmailBody = useMemo(() => [
        'Please find the requested Watch player diagnostics below.',
        '',
        `Program name: ${launchDetails.title ?? 'Not available'}`,
        `Program itemSk: ${launchDetails.itemSk ?? 'Not available'}`,
        `Program itemPk: ${launchDetails.itemPk ?? 'Not available'}`,
        `User name: ${userName ?? 'Not available'}`,
        `User ID: ${individualSk ?? 'Not available'}`,
        '',
        `Watch URL: ${window.location.href}`,
        '',
        'Diagnostics JSON:',
        playerDiagnosticsJson,
    ].join('\n'), [individualSk, launchDetails.itemPk, launchDetails.itemSk, launchDetails.title, playerDiagnosticsJson, userName]);
    const diagnosticsMailtoLink = useMemo(() => buildMailtoLink({
        to: diagnosticsSupportEmailTo,
        subject: diagnosticsSupportEmailSubject,
        body: diagnosticsEmailBody,
    }), [diagnosticsEmailBody, diagnosticsSupportEmailSubject, diagnosticsSupportEmailTo]);
    const canRunPlayPauseTest = !isPlayDisabled || !isPauseDisabled;

    const issueSeekCommand = (targetPosition: number) => {
        dispatch(setExternalSeekCommand(clamp(Math.round(targetPosition), 0, seekUpperBound)));
    };

    const runPlayPauseTest = () => {
        if (!isPlayDisabled) {
            dispatch(setExternalCommand(ExternalCommand.play));
            return;
        }

        if (!isPauseDisabled) {
            dispatch(setExternalCommand(ExternalCommand.pause));
        }
    };

    const savePlayerDiagnosticsJson = () => {
        const blob = new Blob([playerDiagnosticsJson], { type: 'application/json' });
        const downloadUrl = URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

        downloadLink.href = downloadUrl;
        downloadLink.download = `watch-player-diagnostics-${timestamp}.json`;
        downloadLink.click();

        URL.revokeObjectURL(downloadUrl);
    };

    const emailDiagnostics = () => {
        openMailtoLink(diagnosticsMailtoLink);
    };

    const runRangeProbeTest = async () => {
        await runRangeProbe();
    };

    const runSeekTest = (targetPosition: number) => {
        issueSeekCommand(targetPosition);
    };

    const runPlayCommand = () => {
        dispatch(setExternalCommand(ExternalCommand.play));
    };

    const runPauseCommand = () => {
        dispatch(setExternalCommand(ExternalCommand.pause));
    };

    return (
        <div className="debug support-panel__sub-panel support-panel__diagnostics-panel">
            <h3 className="support-panel__sub-panel__title support-panel__diagnostics-title">Player diagnostics</h3>
            <p className="support-panel__diagnostics-intro">
                This panel helps diagnose playback, pause, or seeking issues. Customer Service may walk you through it or ask you to share the results during troubleshooting.
            </p>

            <div className="support-panel__diagnostics-layout">
                <DiagnosticCard
                    action={(
                        <button
                            className="support-panel__diagnostics-run-button"
                            disabled={!diagnostics.currentFile || isRunningRangeProbe}
                            onClick={runRangeProbeTest}
                            type="button"
                        >
                            Run test
                        </button>
                    )}
                    status={<StatusIcon label={rangeProbeStatus.label} tone={rangeProbeStatus.tone} />}
                    title="Range probe"
                    defaultExpanded={false}
                >
                    <div className="support-panel__diagnostics-actions support-panel__diagnostics-actions--row">
                        <button
                            className="support-panel__debug-button support-panel__debug-button--secondary"
                            disabled={!diagnostics.currentFile || isRunningRangeProbe}
                            onClick={runRangeProbeTest}
                        >
                            Run range probe
                        </button>
                    </div>
                    <div className="support-panel__diagnostic-grid">
                        <DiagnosticSplitRow
                            firstLabel="Range probe status"
                            firstValue={diagnostics.rangeProbe.status}
                            secondLabel="Range probe HTTP status"
                            secondValue={formatDebugValue(diagnostics.rangeProbe.responseStatus)}
                        />
                        {/* TODO: Re-enable these header diagnostics after the media server exposes them via CORS so browser JS can read them reliably. */}
                        {/*
                        <DiagnosticRow label="Accept-Ranges" value={formatDebugValue(diagnostics.rangeProbe.acceptRanges)} />
                        <DiagnosticRow
                            label="Content-Range"
                            value={formatDebugValue(diagnostics.rangeProbe.contentRange)}
                            valueClassName="support-panel__debug-code"
                        />
                        */}
                        <DiagnosticRow
                            label="Range probe checked"
                            value={formatDebugValue(diagnostics.rangeProbe.checkedAt)}
                        />
                        <DiagnosticRow
                            label="Range probe system message"
                            value={formatOptionalMessage(diagnostics.rangeProbe.error)}
                        />
                    </div>
                </DiagnosticCard>
                {showSeekTest && (
                    <DiagnosticCard
                        action={(
                            <button
                                className="support-panel__diagnostics-run-button"
                                disabled={!canSeek}
                                onClick={() => runSeekTest(currentDuration / 2)}
                                type="button"
                            >
                                Run test
                            </button>
                        )}
                        status={<StatusIcon label={lastSeekStatus.label} tone={lastSeekStatus.tone} />}
                        title="Seek test"
                        defaultExpanded={false}
                    >
                        <div className="support-panel__diagnostics-actions support-panel__diagnostics-actions--row">
                            <button
                                className="support-panel__debug-button support-panel__debug-button--secondary"
                                disabled={!canSeek}
                                onClick={() => runSeekTest(Math.max(currentPosition - 30, 0))}
                            >
                                Seek -30s
                            </button>
                            <button
                                className="support-panel__debug-button support-panel__debug-button--secondary"
                                disabled={!canSeek}
                                onClick={() => runSeekTest(Math.min(currentPosition + 30, seekUpperBound))}
                            >
                                Seek +30s
                            </button>
                            <button
                                className="support-panel__debug-button support-panel__debug-button--secondary"
                                disabled={!canSeek}
                                onClick={() => runSeekTest(currentDuration / 2)}
                            >
                                Seek to midpoint
                            </button>
                        </div>
                        <div className="support-panel__diagnostic-grid support-panel__diagnostic-grid--compact">
                            <DiagnosticSplitRow
                                firstLabel="Last seek source"
                                firstValue={formatDebugValue(diagnostics.lastSeek.source)}
                                secondLabel="Last seek status"
                                secondValue={diagnostics.lastSeek.status}
                            />
                            <DiagnosticSplitRow
                                firstLabel="Last seek target"
                                firstValue={formatDebugValue(
                                    diagnostics.lastSeek.targetPosition !== null
                                        ? formatSeconds(diagnostics.lastSeek.targetPosition)
                                        : null
                                )}
                                secondLabel="Last seek observed position"
                                secondValue={formatDebugValue(
                                    diagnostics.lastSeek.observedPosition !== null
                                        ? formatSeconds(diagnostics.lastSeek.observedPosition)
                                        : null
                                )}
                            />
                            <DiagnosticRow
                                label="Last seek failure reason"
                                value={formatOptionalMessage(diagnostics.lastSeek.failureReason)}
                            />
                        </div>
                    </DiagnosticCard>
                )}

                <DiagnosticCard
                    action={(
                        <button
                            className="support-panel__diagnostics-run-button"
                            disabled={!canRunPlayPauseTest}
                            onClick={runPlayPauseTest}
                            type="button"
                        >
                            Run test
                        </button>
                    )}
                    status={<StatusIcon label={lastCommandStatus.label} tone={lastCommandStatus.tone} />}
                    title="Play/Pause test"
                    defaultExpanded={false}
                >
                    <div className="support-panel__diagnostics-actions support-panel__diagnostics-actions--row">
                        <button
                            className="support-panel__debug-button support-panel__debug-button--secondary"
                            disabled={isPlayDisabled}
                            onClick={runPlayCommand}
                        >
                            Play
                        </button>
                        <button
                            className="support-panel__debug-button support-panel__debug-button--secondary"
                            disabled={isPauseDisabled}
                            onClick={runPauseCommand}
                        >
                            Pause
                        </button>
                    </div>
                    <div className="support-panel__diagnostic-grid support-panel__diagnostic-grid--compact">
                        <DiagnosticSplitRow
                            firstLabel="Last command"
                            firstValue={formatDebugValue(playPauseCommand.command)}
                            secondLabel="Last command status"
                            secondValue={playPauseCommand.status}
                        />
                        <DiagnosticRow
                            label="Last command note"
                            value={formatDebugValue(playPauseCommand.details)}
                        />
                    </div>
                </DiagnosticCard>

                <DiagnosticCard
                    action={<EmailIcon />}
                    headerButton={{
                        ariaLabel: 'Share diagnostics with PLI Customer Service',
                        onClick: emailDiagnostics,
                    }}
                    isExpandable={false}
                    showToggle={false}
                    title="Share with PLI's Customer Service"
                    defaultExpanded={false}
                />

                <DiagnosticCard
                    title="Developer Tools"
                    defaultExpanded={false}
                >
                    <div className="support-panel__diagnostics-toolbar">
                        <button
                            className="support-panel__debug-button support-panel__debug-button--secondary"
                            onClick={() => setShowPlayerDiagnostics(!showPlayerDiagnostics)}
                        >
                            {showPlayerDiagnostics ? 'Hide diagnostics' : 'Show diagnostics'}
                        </button>
                        <button
                            className="support-panel__debug-button support-panel__debug-button--secondary"
                            onClick={() => navigator.clipboard.writeText(playerDiagnosticsJson)}
                        >
                            Copy diagnostics
                        </button>
                        <button
                            className="support-panel__debug-button support-panel__debug-button--secondary"
                            onClick={savePlayerDiagnosticsJson}
                        >
                            Download diagnostics
                        </button>
                    </div>

                    {showPlayerDiagnostics && (
                        <pre className="support-panel__diagnostics-json paragraph-1">
                            {playerDiagnosticsJson}
                        </pre>
                    )}

                    <div className="support-panel__diagnostics-developer-section">
                        <span className="support-panel__debug-label">Interpretation</span>
                    </div>
                    <div className="support-panel__diagnostic-grid">
                        {interpretations.map((interpretation) => (
                            <div
                                className="support-panel__diagnostic-row"
                                key={interpretation.id}
                            >
                                <div className="support-panel__diagnostic-interpretation-header">
                                    <span className="support-panel__debug-label">{interpretation.title}</span>
                                    <StatusBadge
                                        label={getInterpretationToneLabel(interpretation.tone)}
                                        tone={interpretation.tone}
                                    />
                                </div>
                                <span>{interpretation.message}</span>
                            </div>
                        ))}
                    </div>

                    <div className="support-panel__diagnostics-developer-section">
                        <span className="support-panel__debug-label">Current playback</span>
                    </div>
                    <div className="support-panel__diagnostic-grid">
                        <DiagnosticRow
                            label="Playback state"
                            value={diagnostics.playbackState}
                        />
                        <DiagnosticSplitRow
                            firstLabel="Current position"
                            firstValue={formatSeconds(currentPosition)}
                            secondLabel="Current Media Duration"
                            secondValue={formatSeconds(currentDuration)}
                        />
                        <DiagnosticSplitRow
                            firstLabel="Seekable range"
                            firstValue={formatTimeRangeSnapshot(diagnostics.browserMedia.seekable)}
                            secondLabel="Buffered range"
                            secondValue={formatTimeRangeSnapshot(diagnostics.browserMedia.buffered)}
                        />
                        <DiagnosticSplitRow
                            firstLabel="Ready state"
                            firstValue={getReadyStateLabel(diagnostics.browserMedia.readyState)}
                            secondLabel="Network state"
                            secondValue={getNetworkStateLabel(diagnostics.browserMedia.networkState)}
                        />
                        <DiagnosticSplitRow
                            firstLabel="Paused"
                            firstValue={formatBoolean(diagnostics.browserMedia.paused)}
                            secondLabel="Ended"
                            secondValue={formatBoolean(diagnostics.browserMedia.ended)}
                        />
                        <DiagnosticSplitRow
                            firstLabel="Native error code"
                            firstValue={formatDebugValue(diagnostics.browserMedia.nativeError.code)}
                            secondLabel="Signals last updated"
                            secondValue={formatDebugValue(diagnostics.browserMedia.lastUpdatedAt)}
                        />
                        <DiagnosticRow
                            label="Native error message"
                            value={formatOptionalMessage(diagnostics.browserMedia.nativeError.message)}
                        />
                    </div>
                </DiagnosticCard>
            </div>
        </div>
    );
};

export default PlayerDiagnostics;
